import React, { useEffect, useRef } from 'react';
import { View, StyleSheet, Animated, Easing } from 'react-native';
import { PAYMENT_DESIGN_TOKENS, PAYMENT_SPACING, PAYMENT_BORDER_RADIUS, PAYMENT_AVATAR_SIZE } from '@/constants/residents/mobile/paymentDesignTokens';

/**
 * Skeleton loading placeholder for MobilePaymentCardV2
 * Shows animated shimmer effect while data is loading
 */
export const MobilePaymentCardSkeleton: React.FC = () => {
  const shimmerAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const animation = Animated.loop(
      Animated.sequence([
        Animated.timing(shimmerAnim, {
          toValue: 1,
          duration: 1000,
          easing: Easing.ease,
          useNativeDriver: true,
        }),
        Animated.timing(shimmerAnim, {
          toValue: 0,
          duration: 1000,
          easing: Easing.ease,
          useNativeDriver: true,
        }),
      ])
    );
    animation.start();
    return () => animation.stop();
  }, [shimmerAnim]);

  const opacity = shimmerAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [0.3, 0.7],
  });

  const SkeletonBox: React.FC<{ width: number | string; height: number; style?: any }> = ({ width, height, style }) => (
    <Animated.View
      style={[
        styles.skeletonBox,
        { width, height, opacity },
        style,
      ]}
    />
  );

  return (
    <View style={styles.cardWrapper}>
      <View style={styles.card}>
        <View style={styles.cardContent}>
          {/* Top Section - Avatar and Info */}
          <View style={styles.topSection}>
            {/* Avatar Skeleton */}
            <Animated.View style={[styles.avatarSkeleton, { opacity }]} />

            {/* Info Section Skeleton */}
            <View style={styles.infoSection}>
              {/* Badge Skeleton */}
              <SkeletonBox width={90} height={24} style={styles.badgeSkeleton} />
              
              {/* Name Skeleton */}
              <SkeletonBox width={140} height={22} style={{ marginTop: PAYMENT_SPACING.xs }} />
              
              {/* Property Skeleton */}
              <SkeletonBox width={180} height={16} style={{ marginTop: PAYMENT_SPACING.xs }} />
              
              {/* Status Message Skeleton - 2 lines */}
              <SkeletonBox width="100%" height={14} style={{ marginTop: PAYMENT_SPACING.sm }} />
              <SkeletonBox width="80%" height={14} style={{ marginTop: 6 }} />
            </View>
          </View>

          {/* Bottom Section */}
          <View style={styles.bottomSection}>
            {/* Separator Line */}
            <View style={styles.separatorLine} />

            {/* Balance Section Skeleton */}
            <View style={styles.balanceSection}>
              <View style={styles.balanceRow}>
                <SkeletonBox width={100} height={32} />
                <Animated.View style={[styles.infoIconSkeleton, { opacity }]} />
              </View>
              <SkeletonBox width={80} height={14} style={{ marginTop: 4 }} />
            </View>

            {/* Autopay Link Skeleton */}
            <SkeletonBox width={100} height={16} style={{ marginBottom: PAYMENT_SPACING.md }} />

            {/* Button Skeleton */}
            <SkeletonBox width={120} height={34} style={styles.buttonSkeleton} />
          </View>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  cardWrapper: {
    marginBottom: PAYMENT_SPACING.lg,
  },
  card: {
    backgroundColor: PAYMENT_DESIGN_TOKENS.CARD_BACKGROUND,
    borderRadius: PAYMENT_BORDER_RADIUS.xl,
    overflow: 'hidden',
  },
  cardContent: {
    padding: PAYMENT_SPACING.xl,
  },
  topSection: {
    flexDirection: 'row',
    gap: 18,
  },
  avatarSkeleton: {
    width: PAYMENT_AVATAR_SIZE.lg,
    height: PAYMENT_AVATAR_SIZE.lg,
    borderRadius: PAYMENT_AVATAR_SIZE.lg / 2,
    backgroundColor: PAYMENT_DESIGN_TOKENS.BORDER,
  },
  infoSection: {
    flex: 1,
    gap: PAYMENT_SPACING.xs,
  },
  skeletonBox: {
    backgroundColor: PAYMENT_DESIGN_TOKENS.BORDER,
    borderRadius: 4,
  },
  badgeSkeleton: {
    borderRadius: 60,
  },
  bottomSection: {
    marginLeft: PAYMENT_AVATAR_SIZE.lg + 18,
    paddingTop: PAYMENT_SPACING.lg,
  },
  separatorLine: {
    height: 1,
    backgroundColor: PAYMENT_DESIGN_TOKENS.BORDER,
    marginBottom: PAYMENT_SPACING.lg,
  },
  balanceSection: {
    marginBottom: 18,
  },
  balanceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  infoIconSkeleton: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: PAYMENT_DESIGN_TOKENS.BORDER,
  },
  buttonSkeleton: {
    borderRadius: PAYMENT_BORDER_RADIUS.sm,
  },
});

export default MobilePaymentCardSkeleton;
